import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { passwordResetAPI } from '../utils/api';

function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [step, setStep] = useState('loading'); // loading | password | otp | success | error
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  const verifyToken = useCallback(async () => {
    if (!token) {
      setTokenError('No reset token provided.');
      setStep('error');
      return;
    }

    try {
      await passwordResetAPI.verifyResetToken(token);
      setStep('password');
    } catch (err) {
      setTokenError(err.response?.data?.error || 'Invalid or expired reset link.');
      setStep('error');
    }
  }, [token]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // OTP countdown timer
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const timer = setInterval(() => {
      setOtpCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCountdown]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await passwordResetAPI.resetPassword(token, password);
      setStep('otp');
      setOtpCountdown(5 * 60); // 5 minutes
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong.';
      if (msg.includes('expired') || msg.includes('Invalid')) {
        setTokenError(msg);
        setStep('error');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await passwordResetAPI.confirmOtp(token, otp);
      setStep('success');
    } catch (err) {
      const msg = err.response?.data?.error || 'Verification failed.';
      if (msg.includes('start over') || msg.includes('expired')) {
        setTokenError(msg);
        setStep('error');
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center p-4">
      <div className="card w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TCS Insight" className="h-20 w-auto mx-auto mb-4" />
          <p className="text-gray-600">MPAMOT Digital Solutions</p>
        </div>

        {/* Loading */}
        {step === 'loading' && (
          <div className="text-center py-8">
            <p className="text-gray-600">Verifying reset link...</p>
          </div>
        )}

        {/* Error / Invalid token */}
        {step === 'error' && (
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {tokenError}
            </div>
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
              Request a new reset link
            </Link>
          </div>
        )}

        {/* Step 1: New Password */}
        {step === 'password' && (
          <>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Set New Password</h2>
            <p className="text-sm text-gray-500 mb-6">Enter your new password below.</p>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input"
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input"
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Processing...' : 'Continue'}
              </button>
            </form>
          </>
        )}

        {/* Step 2: OTP Verification */}
        {step === 'otp' && (
          <>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Enter Verification Code</h2>
            <p className="text-sm text-gray-500 mb-6">
              We've sent a 6-digit code to your email. Enter it below to confirm your password change.
            </p>

            {otpCountdown > 0 && (
              <div className="text-center mb-4">
                <span className="text-sm text-gray-500">Code expires in </span>
                <span className="text-sm font-semibold text-primary-600">{formatTime(otpCountdown)}</span>
              </div>
            )}

            {otpCountdown === 0 && step === 'otp' && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-4 text-sm">
                Your verification code may have expired.{' '}
                <Link to="/forgot-password" className="font-medium underline">Request a new one</Link>.
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                {error}
              </div>
            )}

            <form onSubmit={handleOtpSubmit} className="space-y-4">
              <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700 mb-1">
                  Verification Code
                </label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input text-center text-2xl tracking-widest"
                  placeholder="000000"
                  required
                  maxLength={6}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || otp.length !== 6}
                className="btn btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify & Reset Password'}
              </button>
            </form>
          </>
        )}

        {/* Step 3: Success */}
        {step === 'success' && (
          <div className="text-center">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
              Your password has been reset successfully!
            </div>
            <Link
              to="/login"
              className="btn btn-primary inline-block"
            >
              Go to Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
